import type { ComponentProps, ReactNode } from "react";

import IconLoader from "@ui/central-icons/icons/IconLoader";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import clsx from "clsx";

import { PreviewPlayerIcon } from "./PreviewPlayerIcon";
import { PreviewPlayerProgressRing } from "./PreviewPlayerProgressRing";
import type { PreviewPlayerSize } from "./previewPlayerSizeConfig";
import { PREVIEW_PLAYER_SIZE_CONFIG } from "./previewPlayerSizeConfig";

interface PreviewPlayerButtonProps extends ComponentProps<"div"> {
  children?: ReactNode;
  inverted?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
  /** Render the circular playback-progress ring around the button. Set false for a plain badge. */
  showRing?: boolean;
  size?: PreviewPlayerSize;
}

export function PreviewPlayerButton({
  children,
  inverted = false,
  isActive = false,
  isLoading = false,
  showRing = true,
  size = "md",
  className,
  ...props
}: PreviewPlayerButtonProps) {
  const config = PREVIEW_PLAYER_SIZE_CONFIG[size];

  const progressRing =
    children !== undefined && children !== null && children !== false ? (
      children
    ) : (
      <PreviewPlayerProgressRing isActive={isActive} inverted={inverted} size={size} />
    );

  return (
    <div
      className={clsx(
        "tw-group/player tw-relative tw-isolate tw-flex tw-items-center tw-justify-center",
        config.container,
        className,
      )}
      {...props}
    >
      <div
        className={clsx("peer/button tw-relative tw-z-10 tw-flex tw-items-center tw-justify-center", config.container)}
      >
        <div
          className={clsx(
            "tw-absolute tw-inset-0 tw-rounded-full",
            inverted ? "tw-bg-black" : "tw-bg-white",
            inverted
              ? "tw-shadow-[0px_0px_0px_1px_rgba(255,255,255,0.1),0px_1px_2px_0px_rgba(0,0,0,0.2),0px_2px_4px_0px_rgba(0,0,0,0.2)]"
              : "tw-shadow-[0px_0px_0px_1px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_4px_0px_rgba(0,0,0,0.04)]",
            "tw-transition-transform tw-duration-500 tw-ease-[cubic-bezier(0.16,1,0.3,1)]",
            "group-hover/player:tw-scale-[0.925926]",
            "group-focus-visible/preview-button:tw-scale-[0.925926]",
            isActive && "tw-scale-[0.925926]",
          )}
        />
        <div className={clsx("tw-relative tw-z-10", inverted ? "tw-text-white" : "tw-text-black")}>
          {isLoading ? (
            <IconLoader className={clsx(iconStrokeFix, config.spinner, "tw-animate-spin")} />
          ) : (
            <PreviewPlayerIcon isActive={isActive} size={size} />
          )}
        </div>
      </div>
      {showRing && progressRing}
    </div>
  );
}
